import React, { useState, useRef, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdLayout from "../components/AdLayout";
import JoditEditor from "jodit-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../axiosInstance";
import { API_URL_All } from "../api";
import "../css/newsEditor-new.css";

const NewsEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Ref to store editor content temporarily (avoid re-render)
  const contentRef = useRef({});
  const pendingImageAltQueueRef = useRef([]);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    category: "tin-tuc-thi-truong",
    categoryLabel: "Tin tức thị trường",
    date: new Date().toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }),
    author: "ZTO Việt Nam",
    excerpt: "",
    image: null,
    imagePreview: null,
    featured: false,
    showTableOfContents: true,
    tags: [], // Each tag: { text: string, link: string } (link kept for backward compatibility)
    referenceLinks: [],
    content: [
      {
        id: "section-1",
        title: "",
        content: "",
        subSections: [],
      },
    ],
  });

  // Categories
  const categories = [
    { value: "tin-tuc-thi-truong", label: "Tin tức thị trường" },
    { value: "kien-thuc-xnk", label: "Kiến thức XNK" },
    { value: "nguon-hang", label: "Nguồn hàng" },
  ];

  // Load existing article if editing
  useEffect(() => {
    const loadArticle = async () => {
      if (id && id !== "new") {
        setIsLoading(true);
        try {
          const response = await axiosInstance.get(`${API_URL_All}/api/news/${id}`);
          const article = response.data;

          const loadedContent = article.content && article.content.length > 0
            ? article.content.map(section => ({
                ...section,
                subSections: section.subSections || [],
              }))
            : [
                {
                  id: "section-1",
                  title: "",
                  content: "",
                  subSections: [],
                },
              ];

          setFormData({
            title: article.title || "",
            category: article.categorySlug || "tin-tuc-thi-truong",
            categoryLabel: article.category || "Tin tức thị trường",
            date: article.publishDate || new Date().toLocaleDateString("vi-VN"),
            author: article.author || "ZTO Việt Nam",
            excerpt: article.excerpt || "",
            image: null,
            imagePreview: article.imageUrl || null,
            featured: article.featured || false,
            showTableOfContents: article.showTableOfContents ?? true,
            // Handle backward compatibility: convert old string tags to new format
            tags: (article.tags || []).map(tag => 
              typeof tag === 'string' ? { text: tag, link: '' } : tag
            ),
            referenceLinks: article.referenceLinks || [],
            content: loadedContent,
          });

          // Initialize contentRef with loaded content
          loadedContent.forEach((section, index) => {
            contentRef.current[`section-${index}`] = section.content;
            section.subSections.forEach((sub, subIndex) => {
              contentRef.current[`section-${index}-sub-${subIndex}`] = sub.content;
            });
          });
        } catch (error) {
          console.error("Error loading article:", error);
          toast.error("Không thể tải bài viết!");
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    loadArticle();
  }, [id]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle category change
  const handleCategoryChange = (e) => {
    const selectedCategory = categories.find(
      (cat) => cat.value === e.target.value,
    );
    setFormData((prev) => ({
      ...prev,
      category: selectedCategory.value,
      categoryLabel: selectedCategory.label,
    }));
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          image: file,
          imagePreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const createImageAltFromFileName = (fileName) =>
    fileName.replace(/\.[^.]+$/, "").trim();

  useEffect(() => {
    const collectEditorUploadNames = (files) => {
      const fileNames = Array.from(files || [])
        .map((file) => createImageAltFromFileName(file.name))
        .filter(Boolean);

      if (fileNames.length > 0) {
        pendingImageAltQueueRef.current.push(...fileNames);
      }
    };

    const handleEditorFileInputChange = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || target.type !== "file") {
        return;
      }

      if (!target.closest(".jodit-container, .jodit-dialog, .jodit")) {
        return;
      }

      collectEditorUploadNames(target.files);
    };

    const handleEditorDrop = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      if (!target.closest(".jodit-container, .jodit-dialog, .jodit")) {
        return;
      }

      collectEditorUploadNames(event.dataTransfer?.files);
    };

    document.addEventListener("change", handleEditorFileInputChange, true);
    document.addEventListener("drop", handleEditorDrop, true);
    return () => {
      document.removeEventListener("change", handleEditorFileInputChange, true);
      document.removeEventListener("drop", handleEditorDrop, true);
    };
  }, []);

  // Handle section changes - store in ref, not state (avoid re-render)
  const handleSectionChange = (index, field, value) => {
    if (field === "content") {
      // Store content in ref to avoid re-render
      contentRef.current[`section-${index}`] = value;
    } else {
      // For title, update state normally
      const newContent = [...formData.content];
      newContent[index][field] = value;
      setFormData((prev) => ({
        ...prev,
        content: newContent,
      }));
    }
  };

  // Add new section
  const addSection = () => {
    const newIndex = formData.content.length;
    const newSection = {
      id: `section-${newIndex + 1}`,
      title: "",
      content: "",
      subSections: [],
    };
    contentRef.current[`section-${newIndex}`] = "";
    setFormData((prev) => ({
      ...prev,
      content: [...prev.content, newSection],
    }));
  };

  // Add sub-section to a section
  const addSubSection = (sectionIndex) => {
    const newContent = [...formData.content];
    const section = newContent[sectionIndex];
    const subIndex = section.subSections.length;
    const newSubSection = {
      id: `${section.id}-sub-${subIndex + 1}`,
      title: "",
      content: "",
    };
    contentRef.current[`section-${sectionIndex}-sub-${subIndex}`] = "";
    section.subSections = [...section.subSections, newSubSection];
    setFormData((prev) => ({
      ...prev,
      content: newContent,
    }));
  };

  // Handle sub-section changes - store in ref for content
  const handleSubSectionChange = (sectionIndex, subIndex, field, value) => {
    if (field === "content") {
      // Store content in ref to avoid re-render
      contentRef.current[`section-${sectionIndex}-sub-${subIndex}`] = value;
    } else {
      // For title, update state normally
      const newContent = [...formData.content];
      newContent[sectionIndex].subSections[subIndex][field] = value;
      setFormData((prev) => ({
        ...prev,
        content: newContent,
      }));
    }
  };

  // Remove sub-section
  const removeSubSection = (sectionIndex, subIndex) => {
    const newContent = [...formData.content];
    newContent[sectionIndex].subSections = newContent[sectionIndex].subSections.filter(
      (_, i) => i !== subIndex
    );
    setFormData((prev) => ({
      ...prev,
      content: newContent,
    }));
  };

  // Remove section
  const removeSection = (index) => {
    if (formData.content.length > 1) {
      const newContent = formData.content.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        content: newContent,
      }));
    } else {
      toast.warning("Phải có ít nhất một section!");
    }
  };

  // Sanitize HTML content - remove empty tags and whitespace
  const sanitizeContent = (htmlContent) => {
    if (!htmlContent) return "";

    // Create a temporary div to parse HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    const images = tempDiv.querySelectorAll("img");
    images.forEach((img, index) => {
      img.removeAttribute("width");
      img.removeAttribute("height");

      img.style.width = "100%";
      img.style.maxWidth = "100%";
      img.style.height = "auto";
      img.style.display = "block";
      img.style.margin = "16px auto";

      const currentAlt = img.getAttribute("alt")?.trim();
      if (!currentAlt) {
        const src = img.getAttribute("src") || "";
        let fallbackAlt = `Hinh anh ${index + 1}`;

        if (src && !src.startsWith("data:")) {
          const fileName = src.split("/").pop()?.split("?")[0];
          if (fileName) {
            fallbackAlt = decodeURIComponent(fileName).replace(/\.[^.]+$/, "");
          }
        }

        img.setAttribute("alt", fallbackAlt);
      }
    });

    // Get text content (strips all HTML)
    const textContent = tempDiv.textContent || tempDiv.innerText || "";

    // Keep sections that only contain images
    if (!textContent.trim() && images.length === 0) {
      return "";
    }

    return tempDiv.innerHTML;
  };

  // Sync preview - update state from ref when user clicks preview icon
  const syncPreview = () => {
    setFormData((prev) => {
      const newContent = prev.content.map((section, index) => ({
        ...section,
        content: sanitizeContent(contentRef.current[`section-${index}`] || section.content),
        subSections: section.subSections.map((sub, subIndex) => ({
          ...sub,
          content: sanitizeContent(
            contentRef.current[`section-${index}-sub-${subIndex}`] || sub.content
          ),
        })),
      }));
      return {
        ...prev,
        content: newContent,
      };
    });
    toast.success("Preview đã được cập nhật!");
  };

  // Handle save
  const handleSave = async () => {
    // Validation
    if (!formData.title.trim()) {
      toast.error("Vui lòng nhập tiêu đề bài viết!");
      return;
    }
    if (!formData.excerpt.trim()) {
      toast.error("Vui lòng nhập mô tả ngắn!");
      return;
    }

    setIsSaving(true);
    try {
      // Merge content from ref into formData before saving
      const mergedContent = formData.content.map((section, index) => ({
        ...section,
        content: contentRef.current[`section-${index}`] || section.content,
        subSections: section.subSections.map((sub, subIndex) => ({
          ...sub,
          content: contentRef.current[`section-${index}-sub-${subIndex}`] || sub.content,
        })),
      }));

      // Sanitize content before saving
      const sanitizedContent = mergedContent.map(section => ({
        ...section,
        content: sanitizeContent(section.content),
        subSections: section.subSections.map(sub => ({
          ...sub,
          content: sanitizeContent(sub.content)
        }))
      }));

      // Prepare data
      const articleData = {
        title: formData.title,
        category: formData.categoryLabel,
        categorySlug: formData.category,
        publishDate: formData.date,
        author: formData.author,
        excerpt: formData.excerpt,
        imageUrl: formData.imagePreview, // Will be base64 or URL
        featured: formData.featured,
        showTableOfContents: formData.showTableOfContents,
        tags: formData.tags,
        referenceLinks: formData.referenceLinks,
        content: sanitizedContent,
      };

      // Convert image to base64 if it's a file
      if (formData.image) {
        const reader = new FileReader();
        reader.onloadend = async () => {
          articleData.imageUrl = reader.result;
          await saveArticle(articleData);
        };
        reader.readAsDataURL(formData.image);
      } else {
        await saveArticle(articleData);
      }
    } catch (error) {
      toast.error("Có lỗi xảy ra khi lưu bài viết!");
      console.error(error);
      setIsSaving(false);
    }
  };

  // Helper function to save article
  const saveArticle = async (articleData) => {
    try {
      let response;
      if (id === "new") {
        response = await axiosInstance.post(`${API_URL_All}/api/news`, articleData);
        toast.success("Tạo bài viết thành công!");
      } else {
        response = await axiosInstance.put(
          `${API_URL_All}/api/news/${id}`,
          articleData,
        );
        toast.success("Cập nhật bài viết thành công!");
      }

      setTimeout(() => {
        navigate("/admin/news-list");
      }, 1000);
    } catch (error) {
      toast.error("Có lỗi xảy ra khi lưu bài viết!");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  // Jodit Editor configuration
  const joditConfig = useMemo(
    () => ({
      readonly: false,
      placeholder: "Nhập nội dung...",
      height: 400,
      language: "vi",
      imageDefaultWidth: "",
      events: {
        afterInsertImage: (image) => {
          const queuedAlt = pendingImageAltQueueRef.current.shift();
          if (queuedAlt && !image.getAttribute("alt")?.trim()) {
            image.setAttribute("alt", queuedAlt);
          }
        },
      },
      toolbarAdaptive: false,
      toolbarSticky: false,
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      defaultActionOnPaste: "insert_clear_html",
      // Enable realtime updates with debounce for performance
      saveModeInCookie: false,
      // Custom colors
      colors: [
        "#004d9f", "#9bc853", "#000000", "#e60000", "#ff9900", 
        "#ffff00", "#008a00", "#0066cc", "#9933ff", "#ffffff", 
        "#facccc", "#ffebcc", "#ffffcc", "#cce8cc", "#cce0f5", 
        "#ebd6ff", "#bbbbbb", "#f06666", "#ffc266", "#ffff66", 
        "#66b966", "#66a3e0", "#c285ff", "#888888", "#a10000", 
        "#b26b00", "#b2b200", "#006100", "#0047b2", "#6b24b2", 
        "#444444", "#5c0000", "#663d00", "#666600", "#003700", 
        "#002966", "#3d1466"
      ],
      // Toolbar buttons
      buttons: [
        "bold", "italic", "underline", "strikethrough", "|",
        "ul", "ol", "|",
        "font", "fontsize", "brush", "paragraph", "|",
        "image", "table", "link", "|",
        "align", "|",
        "undo", "redo", "|",
        "hr", "eraser", "copyformat", "|",
        "fullsize", "source"
      ],
      // Button configurations for mobile
      buttonsMD: [
        "bold", "italic", "|",
        "ul", "ol", "|",
        "image", "table", "link", "|",
        "brush", "paragraph", "|",
        "align", "|",
        "undo", "redo", "|",
        "eraser", "fullsize"
      ],
      buttonsSM: [
        "bold", "italic", "|",
        "ul", "ol", "|",
        "image", "link", "|",
        "brush", "|",
        "undo", "redo"
      ],
      buttonsXS: [
        "bold", "italic", "|",
        "ul", "ol", "|",
        "image", "|",
        "undo", "redo"
      ],
      // Image upload configuration
      uploader: {
        insertImageAsBase64URI: true,
      },
      // Table configuration - add default border
      table: {
        defaultCellStyle: {
          border: "1px solid #ccc",
          padding: "8px",
        },
      },
      // Create table with border by default
      createAttributes: {
        table: {
          style: "border-collapse: collapse; width: 100%; margin: 10px 0;",
        },
        td: {
          style: "border: 1px solid #d0d0d0; padding: 10px 14px; min-width: 60px;",
        },
        th: {
          style: "border: 1px solid #d0d0d0; padding: 10px 14px; min-width: 60px; background-color: #f8f9fa; font-weight: 600;",
        },
      },
      // Style configurations
      style: {
        font: "14px Arial, sans-serif",
      },
    }),
    []
  );

  return (
    <AdLayout>
      {isLoading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="news-editor-container p-3 p-md-4">
          {/* Header */}
          <div className="bg-white p-4 rounded-3 shadow-sm mb-4">
            <div className="d-flex justify-content-between align-items-center">
              <h2 className="m-0 text-dark fs-2 fw-bold">
                {id === "new" ? "Tạo bài viết mới" : "Chỉnh sửa bài viết"}
              </h2>
              <div className="d-flex gap-2 align-items-center">
                <button
                  className="btn btn-outline-secondary me-2"
                  onClick={() => navigate("/admin/news-list")}
                >
                  <i className="fas fa-arrow-left me-2"></i>
                  Quay lại
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Đang lưu...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Lưu bài viết
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Main Content: 2 Columns */}
          <div className="news-editor-content row g-4">
            {/* Left Column: Form Panel */}
            <div className="col-lg-6">
              <div className="edit-panel bg-white rounded-3 shadow-sm">
                {/* Panel Title */}
                <div className="panel-header d-flex align-items-center px-4 border-bottom border-2">
                  <h4 className="panel-title m-0 fs-5 fw-bold text-dark d-flex align-items-center gap-3">
                    <i className="fas fa-edit text-zto"></i>
                    Thông tin bài viết
                  </h4>
                </div>

                {/* Panel Content - Scrollable */}
                <div className="panel-body p-4">
                  {/* Basic Info Section */}
                  <div className="pb-4">
                    <h5 className="section-header fs-6 fw-bold text-dark mb-4 d-flex align-items-center gap-2 pb-3">
                      <i className="fas fa-info-circle text-zto"></i>
                      Thông tin cơ bản
                    </h5>

                    {/* Title Input */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold text-dark">
                        Tiêu đề <span className="text-danger fw-bold">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Nhập tiêu đề bài viết..."
                        value={formData.title}
                        onChange={(e) =>
                          handleInputChange("title", e.target.value)
                        }
                      />
                    </div>

                    {/* Category */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold text-dark">
                        Danh mục <span className="text-danger fw-bold">*</span>
                      </label>
                      <select
                        className="form-control"
                        value={formData.category}
                        onChange={handleCategoryChange}
                      >
                        {categories.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Author & Date Row */}
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold text-dark">Tác giả</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.author}
                          onChange={(e) =>
                            handleInputChange("author", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold text-dark">Ngày đăng</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.date}
                          onChange={(e) =>
                            handleInputChange("date", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* Excerpt Section */}
                  <div className="pb-4">
                    <div className="mb-3">
                      <label className="form-label fw-semibold text-dark">
                        Mô tả <span className="text-danger fw-bold">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows="10"
                        placeholder="Nhập mô tả ngắn cho bài viết..."
                        value={formData.excerpt}
                        onChange={(e) =>
                          handleInputChange("excerpt", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  {/* Featured Image Section */}
                  <div className="pb-4">
                    <h5 className="section-header fs-6 fw-bold text-dark mb-4 d-flex align-items-center gap-2 pb-3">
                      <i className="fas fa-image text-zto"></i>
                      Ảnh bìa
                    </h5>

                    <div className="mb-3">
                      <label className="form-label fw-semibold text-dark">Chọn ảnh</label>
                      <input
                        type="file"
                        id="coverImageInput"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                      {formData.imagePreview && (
                        <div className="image-preview mt-3 rounded-3 overflow-hidden">
                          <img src={formData.imagePreview} alt={formData.title || "Preview"} />
                        </div>
                      )}
                    </div>

                    {/* Featured Checkbox */}
                    <div className="checkbox-group d-flex align-items-center gap-3 mb-3">
                      <input
                        type="checkbox"
                        id="featuredCheck"
                        checked={formData.featured}
                        onChange={(e) =>
                          handleInputChange("featured", e.target.checked)
                        }
                      />
                      <label htmlFor="featuredCheck" className="m-0 d-flex align-items-center gap-2 fw-medium text-dark">
                        <i className="fas fa-star"></i>
                        Bài viết nổi bật
                      </label>
                    </div>

                    <div className="checkbox-group d-flex align-items-center gap-3 mb-3">
                      <input
                        type="checkbox"
                        id="showTableOfContentsCheck"
                        checked={formData.showTableOfContents}
                        onChange={(e) =>
                          handleInputChange("showTableOfContents", e.target.checked)
                        }
                      />
                      <label
                        htmlFor="showTableOfContentsCheck"
                        className="m-0 d-flex align-items-center gap-2 fw-medium text-dark"
                      >
                        <i className="fas fa-list-ol"></i>
                        Hiển thị mục lục
                      </label>
                    </div>
                  </div>

                  {/* Tags Section */}
                  <div className="pb-4">
                    <h5 className="section-header fs-6 fw-bold text-dark mb-4 d-flex align-items-center gap-2 pb-3">
                      <i className="fas fa-tags text-zto"></i>
                      Từ khóa (Tags)
                    </h5>

                    <div className="mb-3">
                      <label className="form-label fw-semibold text-dark">Thêm từ khóa mới</label>
                      <div className="row g-2 mb-2">
                        <div className="col-md-10">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Nhập từ khóa..."
                            id="tagTextInput"
                          />
                        </div>
                        <div className="col-md-2">
                          <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={() => {
                              const textInput = document.getElementById("tagTextInput");
                              const text = textInput.value.trim();
                              
                              if (text) {
                                // Check if tag with same text already exists
                                if (!formData.tags.some(tag => tag.text === text)) {
                                  handleInputChange("tags", [
                                    ...formData.tags,
                                    { text, link: "" },
                                  ]);
                                }
                                textInput.value = "";
                              }
                            }}
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                        </div>
                      </div>
                      <small className="text-muted">Nhập từ khóa, sau đó click nút + để thêm</small>
                      
                      {/* Display tags */}
                      <div className="d-flex flex-wrap gap-2 mt-3">
                        {formData.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="tag-item px-3 py-2"
                            style={{ fontSize: "13px" }}
                          >
                            {tag.text}
                            <button
                              type="button"
                              className="btn-close btn-close-white ms-2 p-0"
                              style={{
                                fontSize: "10px",
                                height: "16px",
                                width: "16px",
                              }}
                              onClick={() => {
                                const newTags = formData.tags.filter(
                                  (_, i) => i !== index,
                                );
                                handleInputChange("tags", newTags);
                              }}
                            ></button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Reference Links Section */}
                  <div className="pb-4">
                    <h5 className="section-header fs-6 fw-bold text-dark mb-4 d-flex align-items-center gap-2 pb-3">
                      <i className="fas fa-link text-zto"></i>
                      Link tham khảo
                    </h5>

                    <div className="mb-3">
                      <label className="form-label fw-semibold text-dark">Nhập link tham khảo</label>
                      <div className="input-group mb-2">
                        <input
                          type="url"
                          className="form-control"
                          placeholder="https://example.com"
                          id="referenceLinkInput"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && e.target.value.trim()) {
                              e.preventDefault();
                              const newLink = e.target.value.trim();
                              if (!formData.referenceLinks.some(link => link.url === newLink)) {
                                handleInputChange("referenceLinks", [
                                  ...formData.referenceLinks,
                                  { url: newLink, title: "" },
                                ]);
                              }
                              e.target.value = "";
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => {
                            const input = document.getElementById("referenceLinkInput");
                            if (input.value.trim()) {
                              const newLink = input.value.trim();
                              if (!formData.referenceLinks.some(link => link.url === newLink)) {
                                handleInputChange("referenceLinks", [
                                  ...formData.referenceLinks,
                                  { url: newLink, title: "" },
                                ]);
                              }
                              input.value = "";
                            }
                          }}
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                      <small className="text-muted">Nhập URL và nhấn Enter hoặc click nút + để thêm</small>
                      
                      {/* Display reference links */}
                      <div className="mt-3">
                        {formData.referenceLinks.map((link, index) => (
                          <div key={index} className="card mb-2">
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <small className="text-muted fw-semibold">Link {index + 1}</small>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => {
                                    const newLinks = formData.referenceLinks.filter(
                                      (_, i) => i !== index
                                    );
                                    handleInputChange("referenceLinks", newLinks);
                                  }}
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              </div>
                              <div className="mb-2">
                                <input
                                  type="text"
                                  className="form-control form-control-sm mb-2"
                                  placeholder="Tiêu đề link (tùy chọn)..."
                                  value={link.title}
                                  onChange={(e) => {
                                    const newLinks = [...formData.referenceLinks];
                                    newLinks[index].title = e.target.value;
                                    handleInputChange("referenceLinks", newLinks);
                                  }}
                                />
                                <input
                                  type="url"
                                  className="form-control form-control-sm"
                                  value={link.url}
                                  onChange={(e) => {
                                    const newLinks = [...formData.referenceLinks];
                                    newLinks[index].url = e.target.value;
                                    handleInputChange("referenceLinks", newLinks);
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Content Sections */}
                  <div className="pb-0">
                    <div className="d-flex justify-content-between align-items-center gap-3 mb-4">
                      <h5 className="section-header fs-6 fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                        <i className="fas fa-layer-group"></i>
                        Nội dung bài viết
                      </h5>
                      <button className="btn-add-section px-3 py-2 rounded-2 fw-semibold d-inline-flex align-items-center gap-2" onClick={addSection}>
                        <i className="fas fa-plus"></i>
                        Thêm section
                      </button>
                    </div>

                    {formData.content.map((section, index) => (
                      <div key={section.id} className="content-section rounded-3 p-3 mb-3">
                        <div className="section-header-item d-flex justify-content-between align-items-center mb-3 pb-3">
                          <span className="section-number fw-bold small">
                            Section {index + 1}
                          </span>
                          {formData.content.length > 1 && (
                            <button
                              className="btn-remove-section px-2 py-1 rounded-2 d-flex align-items-center gap-2"
                              onClick={() => removeSection(index)}
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          )}
                        </div>

                        {/* Section Title */}
                        <div className="mb-3">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Tiêu đề section..."
                            value={section.title}
                            onChange={(e) =>
                              handleSectionChange(
                                index,
                                "title",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        {/* Section Content */}
                        <div className="mb-3">
                          <JoditEditor
                            value={contentRef.current[`section-${index}`] ?? section.content}
                            config={{
                              ...joditConfig,
                              placeholder: "Nhập nội dung section...",
                            }}
                            tabIndex={1}
                            onChange={(newContent) =>
                              handleSectionChange(index, "content", newContent)
                            }
                            className="jodit-editor"
                          />
                        </div>

                        {/* Add Sub-section Button */}
                        <div className="d-flex justify-content-end mb-3">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
                            onClick={() => addSubSection(index)}
                          >
                            <i className="fas fa-plus"></i>
                            Thêm sub section
                          </button>
                        </div>

                        {/* Sub-sections */}
                        {section.subSections && section.subSections.length > 0 && (
                          <div className="subsections-container ms-4 mt-3">
                            {section.subSections.map((subSection, subIndex) => (
                              <div
                                key={subSection.id}
                                className="subsection-item border-start border-3 border-primary ps-3 mb-3"
                              >
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span className="text-muted small fw-semibold">
                                    Sub-section {index + 1}.{subIndex + 1}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                    onClick={() => removeSubSection(index, subIndex)}
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </div>

                                {/* Sub-section Title */}
                                <div className="mb-2">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Tiêu đề sub-section..."
                                    value={subSection.title}
                                    onChange={(e) =>
                                      handleSubSectionChange(
                                        index,
                                        subIndex,
                                        "title",
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>

                                {/* Sub-section Content */}
                                <div className="mb-0">
                                  <JoditEditor
                                    value={contentRef.current[`section-${index}-sub-${subIndex}`] ?? subSection.content}
                                    config={{
                                      ...joditConfig,
                                      height: 300,
                                      placeholder: "Nhập nội dung sub-section...",
                                    }}
                                    tabIndex={1}
                                    onChange={(newContent) =>
                                      handleSubSectionChange(
                                        index,
                                        subIndex,
                                        "content",
                                        newContent
                                      )
                                    }
                                    className="jodit-editor"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Preview Panel */}
            <div className="col-lg-6">
              <div className="preview-panel bg-white rounded-3 shadow-sm">
                {/* Panel Title */}
                <div className="panel-header d-flex align-items-center px-4 border-bottom border-2">
                  <h4 className="panel-title m-0 fs-5 fw-bold text-dark d-flex align-items-center gap-3">
                    <i 
                      className="fas fa-eye text-primary" 
                      style={{ cursor: "pointer" }}
                      onClick={syncPreview}
                      title="Click để cập nhật preview"
                    ></i>
                    Preview
                  </h4>
                </div>

                {/* Panel Content - Scrollable */}
                <div className="panel-body p-4">
                  <div className="preview-content">
                    {/* Category Badge */}
                    {formData.categoryLabel && (
                      <div className="mb-3">
                        <span className="category-badge px-3 py-2">{formData.categoryLabel}</span>
                      </div>
                    )}

                    {/* Title */}
                    {formData.title && (
                      <h2 className="article-title mb-3 text-zto fw-bold">{formData.title}</h2>
                    )}

                    {/* Meta Info */}
                    <div className="preview-meta d-flex align-items-center flex-wrap gap-3 mb-4 pb-3">
                      {formData.date && (
                        <span className="meta-item">
                          <i className="far fa-calendar"></i>
                          {formData.date}
                        </span>
                      )}
                      {formData.author && (
                        <>
                          <span className="meta-separator">•</span>
                          <span className="meta-item">
                            <i className="far fa-user"></i>
                            {formData.author}
                          </span>
                        </>
                      )}
                      {/* <span className="meta-separator">•</span>
                      <span className="meta-item">
                        <i className="far fa-clock"></i>5 phút đọc
                      </span> */}
                    </div>

                    {/* Featured Image */}
                    {formData.imagePreview && (
                      <div className="preview-image mb-4 rounded-3 overflow-hidden">
                        <img src={formData.imagePreview} alt={formData.title} />
                      </div>
                    )}

                    {/* Excerpt */}
                    {formData.excerpt && (
                      <div className="preview-excerpt p-3 rounded-2 mb-4">
                        <p className="m-0 text-dark lh-lg">{formData.excerpt}</p>
                      </div>
                    )}

                    {/* Content Sections */}
                    {formData.content.map((section, index) => {
                      return (
                        <div key={section.id} className="preview-section mb-5">
                          {section.title && (
                            <h2 className="preview-section-title fs-4 fw-bold pb-3 text-zto">
                              {section.title}
                            </h2>
                          )}
                          {section.content && (
                            <div
                              className="preview-section-content text-dark lh-lg"
                              dangerouslySetInnerHTML={{
                                __html: section.content,
                              }}
                            />
                          )}
                          
                          {/* Sub-sections Preview */}
                          {section.subSections && section.subSections.length > 0 && (
                            <div className="subsections-preview ms-4 mt-4">
                              {section.subSections.map((subSection, subIndex) => {
                                return (
                                  <div key={subSection.id} className="preview-subsection mb-4">
                                    {subSection.title && (
                                      <h3 className="preview-subsection-title fs-5 fw-semibold pb-2 text-zto">
                                        {subSection.title}
                                      </h3>
                                    )}
                                    {subSection.content && (
                                      <div
                                        className="preview-subsection-content text-dark lh-lg"
                                        dangerouslySetInnerHTML={{
                                          __html: subSection.content,
                                        }}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Tags Preview */}
                    {formData.tags && formData.tags.length > 0 && (
                      <div className="card mt-4 mb-4 border-0 shadow-sm">
                        <div className="card-body p-4">
                          <h5 className="card-title mb-3 fw-bold">Từ khóa:</h5>
                          <div className="d-flex flex-wrap gap-2">
                            {formData.tags.map((tag, index) => (
                              <span key={index} className="tag-item px-3 py-2">
                                {tag.text}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Reference Links Preview */}
                    {formData.referenceLinks && formData.referenceLinks.length > 0 && (
                      <div className="card mt-4 mb-4 border-0 shadow-sm">
                        <div className="card-body p-4">
                          <h5 className="card-title mb-3 fw-bold">Link tham khảo:</h5>
                          <ul className="list-unstyled mb-0">
                            {formData.referenceLinks.map((link, index) => (
                              <li key={index} className="mb-2">
                                <a 
                                  href={link.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-primary text-decoration-none d-flex align-items-start gap-2"
                                >
                                  <i className="fas fa-external-link-alt mt-1"></i>
                                  <span>{link.title || link.url}</span>
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* Empty State */}
                    {!formData.title &&
                      !formData.excerpt &&
                      formData.content.every((s) => !s.title && !s.content) && (
                        <div className="preview-empty py-5 px-3 text-center">
                          <i className="fas fa-file-alt d-block mb-3"></i>
                          <p className="m-0">Nhập thông tin bên trái để xem preview</p>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdLayout>
  );
};

export default NewsEditor;
